import { createRouter, createWebHistory } from 'vue-router'
import { useAppStore } from '@/stores/app'
import { hasPaidFeatureAccess, hasVipSitePageAccess } from '@/utils/paidFeatureAccess'
import { reloadOnceForStaleChunk } from '@/utils/chunkLoadRecovery'
import ReservationsView from '@/views/reservations/ReservationsView.vue'
import RootView from '@/views/RootView.vue'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/LoginView.vue'),
    meta: { guest: true },
  },
  {
    path: '/login/callback',
    name: 'LoginCallback',
    component: () => import('@/views/LoginCallbackView.vue'),
    meta: { guest: true },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: { guest: true },
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: { guest: true },
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/RegisterView.vue'),
    meta: { guest: true },
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    component: () => import('@/views/VerifyEmailView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/email/verify',
    name: 'EmailVerifyConfirm',
    component: () => import('@/views/EmailVerifyConfirmView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/email/verify-new',
    name: 'EmailVerifyNew',
    component: () => import('@/views/EmailVerifyNewView.vue'),
    meta: { requiresAuth: false },
  },
  // Root: on main domain = landing page; on restaurant subdomain (e.g. test.rms.local) = that restaurant's public site at /
  // Eager-load so landing renders immediately (no white screen waiting for chunk).
  {
    path: '/',
    name: 'Landing',
    component: RootView,
  },
  // Public restaurant by path: fallback when subdomains aren't used (e.g. localhost/r/demo). Primary UX is subdomain (e.g. demo.rms.local).
  {
    path: '/r/:slug',
    name: 'PublicRestaurant',
    component: () => import('@/views/PublicRestaurantView.vue'),
    props: true,
    meta: { guest: true },
  },
  {
    path: '/app',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true, requiresVerified: true },
    children: [
      {
        path: '',
        name: 'App',
        redirect: () => {
          const appStore = useAppStore()
          return appStore.user?.isSuperadmin ? { name: 'Dashboard' } : { name: 'Restaurants' }
        },
      },
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/DashboardView.vue'),
      },
      {
        path: 'welcome',
        name: 'Welcome',
        component: () => import('@/views/WelcomeView.vue'),
      },
      {
        path: 'restaurants',
        name: 'Restaurants',
        component: () => import('@/views/restaurants/RestaurantListView.vue'),
      },
      {
        path: 'menu-items',
        name: 'MenuItems',
        component: () => import('@/views/menu-items/MenuItemsView.vue'),
      },
      {
        path: 'menu-items/new',
        name: 'MenuItemNew',
        component: () => import('@/views/menu-items/StandaloneMenuItemCreateView.vue'),
      },
      {
        path: 'menu-items/:itemUuid/edit',
        name: 'MenuItemEdit',
        component: () => import('@/views/restaurants/MenuItemFormView.vue'),
        meta: { mode: 'edit', menuItemsModule: true },
      },
      {
        path: 'menu-item-tags',
        name: 'MenuItemTags',
        component: () => import('@/views/MenuItemTagsView.vue'),
      },
      {
        path: 'analytics',
        name: 'Analytics',
        component: () => import('@/views/AnalyticsView.vue'),
      },
      {
        path: 'feedbacks',
        name: 'Feedbacks',
        component: () => import('@/views/feedbacks/FeedbacksLandingView.vue'),
      },
      {
        path: 'announcements',
        name: 'Announcements',
        component: () => import('@/views/announcements/AnnouncementsView.vue'),
      },
      {
        path: 'site-content',
        name: 'SiteContent',
        component: () => import('@/views/site-content/SiteContentView.vue'),
        meta: { requiresVipSitePageAccess: true },
      },
      {
        path: 'site-content/orders',
        name: 'VipOrders',
        component: () => import('@/views/site-content/VipOrdersView.vue'),
        meta: { requiresPaidFeature: 'owner_orders' },
      },
      {
        path: 'vip-site-fields',
        redirect: { name: 'SiteContent' },
      },
      {
        path: 'reservations',
        name: 'Reservations',
        component: ReservationsView,
      },
      {
        path: 'mailing-list',
        name: 'MailingList',
        component: () => import('@/views/MailingListView.vue'),
      },
      {
        path: 'owner-feedback',
        name: 'OwnerFeedback',
        component: () => import('@/views/OwnerFeedbackView.vue'),
      },
      {
        path: 'feedbacks/restaurants/:restaurantUuid',
        name: 'FeedbacksList',
        component: () => import('@/views/feedbacks/FeedbacksListView.vue'),
      },
      {
        path: 'restaurants/new',
        name: 'RestaurantNew',
        component: () => import('@/views/restaurants/RestaurantFormView.vue'),
        meta: { mode: 'create' },
      },
      {
        path: 'restaurants/:uuid',
        name: 'RestaurantDetail',
        component: () => import('@/views/restaurants/RestaurantManageView.vue'),
      },
      {
        path: 'restaurants/:uuid/edit',
        name: 'RestaurantEdit',
        redirect: (to) => ({ name: 'RestaurantDetail', params: to.params, query: { tab: 'profile' } }),
      },
      {
        path: 'restaurants/:uuid/content',
        name: 'RestaurantContent',
        redirect: (to) => ({ name: 'RestaurantDetail', params: to.params, query: { tab: 'settings' } }),
      },
      {
        path: 'restaurants/:uuid/contacts',
        name: 'RestaurantContacts',
        redirect: (to) => ({ name: 'RestaurantDetail', params: to.params, query: { tab: 'profile' } }),
      },
      {
        path: 'restaurants/:uuid/menu-items',
        name: 'RestaurantMenuItems',
        redirect: (to) => ({ name: 'RestaurantDetail', params: to.params, query: { tab: 'menu' } }),
      },
      {
        path: 'restaurants/:uuid/categories/:categoryUuid/items',
        name: 'CategoryMenuItems',
        component: () => import('@/views/restaurants/CategoryMenuItemsView.vue'),
      },
      {
        path: 'restaurants/:uuid/menu-items/new',
        name: 'RestaurantMenuItemNew',
        redirect: () => ({ name: 'MenuItemNew' }),
      },
      {
        path: 'restaurants/:uuid/menu-items/:itemUuid/edit',
        name: 'RestaurantMenuItemEdit',
        component: () => import('@/views/restaurants/MenuItemFormView.vue'),
        meta: { mode: 'edit' },
      },
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/views/ProfileView.vue'),
      },
      {
        path: 'billing',
        name: 'Billing',
        component: () => import('@/views/BillingView.vue'),
      },
      // Superadmin-only routes (guard checks is_superadmin)
      {
        path: 'superadmin/users',
        name: 'SuperadminUsers',
        component: () => import('@/views/superadmin/SuperadminUsersView.vue'),
        meta: { requiresSuperadmin: true },
      },
      {
        path: 'superadmin/owner-feedbacks',
        name: 'SuperadminOwnerFeedbacks',
        component: () => import('@/views/superadmin/SuperadminOwnerFeedbacksView.vue'),
        meta: { requiresSuperadmin: true },
      },
      {
        path: 'superadmin/inquiries',
        name: 'SuperadminInquiries',
        component: () => import('@/views/superadmin/SuperadminInquiriesView.vue'),
        meta: { requiresSuperadmin: true },
      },
      {
        path: 'superadmin/restaurants',
        name: 'SuperadminRestaurants',
        component: () => import('@/views/superadmin/SuperadminRestaurantsView.vue'),
        meta: { requiresSuperadmin: true },
      },
      {
        path: 'superadmin/legal',
        name: 'SuperadminLegal',
        component: () => import('@/views/superadmin/SuperadminLegalView.vue'),
        meta: { requiresSuperadmin: true },
      },
      {
        path: 'superadmin/site-settings',
        name: 'SuperadminSiteSettings',
        component: () => import('@/views/superadmin/SuperadminSiteSettingsView.vue'),
        meta: { requiresSuperadmin: true },
      },
      {
        path: 'superadmin/plans',
        name: 'SuperadminPlans',
        component: () => import('@/views/superadmin/SuperadminPlansView.vue'),
        meta: { requiresSuperadmin: true },
      },
      {
        path: 'superadmin/sms-templates',
        name: 'SuperadminSmsTemplates',
        component: () => import('@/views/superadmin/SuperadminSmsTemplatesView.vue'),
        meta: { requiresSuperadmin: true },
      },
      {
        path: 'superadmin/menu-item-tags',
        name: 'SuperadminMenuItemTags',
        component: () => import('@/views/superadmin/SuperadminMenuItemTagsView.vue'),
        meta: { requiresSuperadmin: true },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { guest: true },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.onError((err) => {
  if (reloadOnceForStaleChunk(err)) return
})

router.beforeEach(async (to) => {
  const appStore = useAppStore()
  const requiresAuth = to.matched.some((r) => r.meta.requiresAuth)
  const requiresVerified = to.matched.some((r) => r.meta.requiresVerified)
  const requiresSuperadmin = to.matched.some((r) => r.meta.requiresSuperadmin)
  const isGuestRoute = to.matched.some((r) => r.meta.guest)
  const needsBlockingAuthDecision = requiresAuth || requiresVerified || requiresSuperadmin
  if (needsBlockingAuthDecision) {
    await appStore.bootstrapAuth()
  } else {
    // Guest/public routes should render immediately; bootstrap runs in background.
    appStore.bootstrapAuth().catch(() => {})
  }
  const isAuthenticated = !!appStore.user
  const isVerified = appStore.user?.isEmailVerified ?? false
  const knownEmail = appStore.user?.email ? String(appStore.user.email) : ''

  if (requiresAuth && !isAuthenticated) {
    return {
      name: 'Login',
      query: {
        redirect: to.fullPath,
        ...(appStore.authBootstrapHadNetworkError ? { message: 'You appear offline. Please reconnect to continue.' } : {}),
      },
    }
  }

  const isPublicRestaurantRoute = to.name === 'PublicRestaurant'
  if (isGuestRoute && isAuthenticated && !isPublicRestaurantRoute) {
    return isVerified ? { name: 'App' } : (knownEmail ? { name: 'VerifyEmail', query: { email: knownEmail } } : { name: 'VerifyEmail' })
  }

  if (requiresVerified && isAuthenticated && !isVerified) {
    return knownEmail ? { name: 'VerifyEmail', query: { email: knownEmail } } : { name: 'VerifyEmail' }
  }

  if (requiresSuperadmin && isAuthenticated && !appStore.user?.isSuperadmin) {
    return { name: 'App' }
  }

  const requiresVipSitePage = to.matched.some((r) => r.meta?.requiresVipSitePageAccess === true)
  if (requiresVipSitePage && isAuthenticated && isVerified && appStore.user) {
    if (!hasVipSitePageAccess(appStore.user)) {
      return { name: 'Dashboard' }
    }
  }

  const paidFeatureMeta = to.matched.find((r) => r.meta?.requiresPaidFeature)?.meta?.requiresPaidFeature
  if (paidFeatureMeta && isAuthenticated && isVerified && appStore.user) {
    if (!hasPaidFeatureAccess(appStore.user, paidFeatureMeta)) {
      return { name: 'Dashboard' }
    }
  }

  if (to.name === 'VerifyEmail' && isAuthenticated && isVerified) {
    return { name: 'App' }
  }
})

export default router
